// Collision in the outdoor field: the player's circle against the features' circles (field.ts layout), found
// through a grid of cells on the torus, so only the circles near the player are tested. No walls: the field repeats,
// and Player wraps the position into [0, size_m). Same interface as the rooms' Collider (player/collision.ts).

import type { WalkCollider } from '../player/collision';
import { wrapDelta, type Circle } from './field';

const CELL = 4;

export class FieldCollider implements WalkCollider {
  private readonly n: number;
  private readonly cell: number;
  /** per cell: indices into xs / zs / rs */
  private readonly cells: Int32Array[];
  private readonly xs: Float64Array;
  private readonly zs: Float64Array;
  private readonly rs: Float64Array;
  private readonly reach: number;
  x = 0;
  z = 0;

  constructor(circles: readonly Circle[], readonly radius: number, readonly period: number) {
    this.n = Math.max(1, Math.floor(period / CELL));
    this.cell = period / this.n;
    const m = circles.length;
    this.xs = new Float64Array(m); this.zs = new Float64Array(m); this.rs = new Float64Array(m);
    const lists: number[][] = Array.from({ length: this.n * this.n }, () => []);
    let reach = 0;
    circles.forEach(([x, z, r], i) => {
      this.xs[i] = x; this.zs[i] = z; this.rs[i] = r;
      reach = Math.max(reach, r);
      const R = r + radius;
      for (let a = Math.floor((x - R) / this.cell); a <= Math.floor((x + R) / this.cell); a++)
        for (let b = Math.floor((z - R) / this.cell); b <= Math.floor((z + R) / this.cell); b++) {
          const l = lists[this.idx(a, b)]!;
          if (l[l.length - 1] !== i && !l.includes(i)) l.push(i);
        }
    });
    this.reach = reach;
    this.cells = lists.map((l) => Int32Array.from(l));
  }

  private idx(a: number, b: number): number {
    const n = this.n;
    return (((a % n) + n) % n) * n + (((b % n) + n) % n);
  }

  /** The circles that may touch the player at (x, z) (every circle whose grown footprint covers that cell). */
  private near(x: number, z: number): Int32Array {
    return this.cells[this.idx(Math.floor(x / this.cell), Math.floor(z / this.cell))]!;
  }

  valid(x: number, z: number): boolean {
    const r = this.radius;
    for (const i of this.near(x, z)) {
      const dx = wrapDelta(x - this.xs[i]!, this.period), dz = wrapDelta(z - this.zs[i]!, this.period);
      if (dx * dx + dz * dz < (r + this.rs[i]!) ** 2 - 1e-9) return false;
    }
    return true;
  }

  /** Move from (x0, z0) toward (x1, z1) in steps of half the radius, pushing out of circles (slides along them). */
  resolve(x0: number, z0: number, x1: number, z1: number): void {
    const r = this.radius;
    const dx = x1 - x0, dz = z1 - z0;
    const steps = Math.max(1, Math.ceil(Math.hypot(dx, dz) / (r * 0.5)));
    let x = x0, z = z0;
    for (let s = 0; s < steps; s++) {
      x += dx / steps;
      z += dz / steps;
      for (let it = 0; it < 4; it++) {
        let moved = false;
        for (const i of this.near(x, z)) {
          const ox = wrapDelta(x - this.xs[i]!, this.period), oz = wrapDelta(z - this.zs[i]!, this.period);
          const R = r + this.rs[i]!;
          const d2 = ox * ox + oz * oz;
          if (d2 >= R * R) continue;
          const d = Math.sqrt(d2);
          // out along the line from the circle's centre (straight back along the move if exactly on it)
          const nx = d > 1e-9 ? ox / d : -dx / (Math.hypot(dx, dz) || 1), nz = d > 1e-9 ? oz / d : -dz / (Math.hypot(dx, dz) || 1);
          x += nx * (R - d + 1e-6);
          z += nz * (R - d + 1e-6);
          moved = true;
        }
        if (!moved) break;
      }
    }
    this.x = x;
    this.z = z;
  }

  /** Nearest free place to (x, z): out of any circle it is in. */
  settle(x: number, z: number): void {
    this.resolve(x, z, x, z);
    for (let k = 0; k < 8 && !this.valid(this.x, this.z); k++) this.resolve(this.x + this.reach * 0.5, this.z, this.x + this.reach * 0.5, this.z);
  }
}
